import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { SkillDefinition } from '@shared/types'
import { readSkillOrder, saveSkillOrder } from '@shared/services'
import { spectrumColor } from '@shared/data/brandColors'
import { SkillGlyph } from '@shared/components/SkillGlyph'

/**
 * The skills a click of the mouse wheel brings up on the board (Huanrui,
 * 2026-10-02).
 *
 * Each person chooses which: it starts as a single + where the wheel was
 * clicked, the + lists every skill in the rail to tick on or off, and the
 * ticked ones line up beside it from then on. Picking one puts it on the
 * board right there, as dragging it out of the rail would. A click anywhere
 * else, Escape or the wheel turning puts the whole thing away.
 *
 * Kept with the person's other skill orders on the server, as the surface
 * "quick" (docs/quick-skills.sql), so it follows them to another machine; read
 * once per visit and kept here after that, so it opens at once every time.
 *
 * Nothing of this is mounted until the wheel is clicked, so the board does no
 * work for it while it is put away.
 *
 * The wheel row was moved onto pictures on 2026-10-07 and brought back the
 * same day: some cards have to be put down from nothing. Both stay -- the
 * row here, and QuickBar below on a chosen picture.
 */

/*
 * Two lists, kept apart (Huanrui, 2026-10-07): the wheel's row ('quick') and
 * the tools on a chosen picture ('quick-picture'). Each is read once per
 * visit and kept here after that.
 */
type QuickSurface = 'quick' | 'quick-picture'
const remembered: Record<QuickSurface, string[] | null> = { quick: null, 'quick-picture': null }
const reading: Record<QuickSurface, Promise<string[]> | null> = { quick: null, 'quick-picture': null }

function readChosen(surface: QuickSurface): Promise<string[]> {
  const known = remembered[surface]
  if (known) return Promise.resolve(known)
  if (!reading[surface]) {
    reading[surface] = readSkillOrder(surface)
      .then((found) => {
        remembered[surface] = found
        return found
      })
      .catch(() => {
        reading[surface] = null
        return remembered[surface] ?? []
      })
  }
  return reading[surface] as Promise<string[]>
}

function keepChosen(surface: QuickSurface, next: string[]): void {
  remembered[surface] = next
  void saveSkillOrder(surface, next).catch((error: unknown) => console.warn('could not keep the quick skills', error))
}

interface QuickSkillsProps {
  /** Every skill the rail offers, in the platform's order (which sets the colours). */
  skills: SkillDefinition[]
  /** Where the wheel was clicked, in the window's coordinates. */
  at: { x: number; y: number }
  onPick: (skill: SkillDefinition, glow: string, clientX: number, clientY: number) => void
  onClose: () => void
}

export function QuickSkills({ skills, at, onPick, onClose }: QuickSkillsProps) {
  const [chosen, setChosen] = useState<string[] | null>(remembered['quick'])
  const [choosing, setChoosing] = useState(false)
  const box = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (chosen) return
    let cancelled = false
    void readChosen('quick').then((found) => {
      if (!cancelled) setChosen(found)
    })
    return () => {
      cancelled = true
    }
  }, [chosen])

  useEffect(() => {
    const away = (event: Event) => {
      if (!box.current?.contains(event.target as Node)) onClose()
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('pointerdown', away, true)
    window.addEventListener('wheel', away, { passive: true })
    window.addEventListener('keydown', escape)
    return () => {
      window.removeEventListener('pointerdown', away, true)
      window.removeEventListener('wheel', away)
      window.removeEventListener('keydown', escape)
    }
  }, [onClose])

  // The same colour each skill has in the rail.
  const glowOf = useMemo(() => {
    const total = Math.max(skills.length, 1)
    return new Map(skills.map((skill, index) => [skill.id, spectrumColor(index, total)]))
  }, [skills])
  const byId = useMemo(() => new Map(skills.map((skill) => [skill.id, skill])), [skills])
  const shown = (chosen ?? []).map((id) => byId.get(id)).filter((skill): skill is SkillDefinition => Boolean(skill))

  const flip = (id: string) => {
    const next = (chosen ?? []).includes(id) ? (chosen ?? []).filter((one) => one !== id) : [...(chosen ?? []), id]
    setChosen(next)
    keepChosen('quick', next)
  }

  // Kept on screen near the edges: the row is centred on the click, the list
  // opens below it, or above when there is no room below.
  const left = Math.min(Math.max(at.x, 140), window.innerWidth - 140)
  const below = at.y < window.innerHeight - 360

  return createPortal(
    <div
      ref={box}
      className={`cquick${below ? '' : ' cquick--up'}`}
      style={{ left, top: at.y }}
      onContextMenu={(event) => event.preventDefault()}
    >
      <div className="cquick__row">
        {shown.map((skill) => (
          <button
            key={skill.id}
            type="button"
            className="cquick__skill"
            style={{ '--tile-glow': glowOf.get(skill.id) } as React.CSSProperties}
            title={skill.summary}
            onClick={() => {
              onPick(skill, glowOf.get(skill.id) ?? spectrumColor(0, 1), at.x, at.y)
              onClose()
            }}
          >
            <span className="cquick__glyph" aria-hidden="true">
              <SkillGlyph glyph={skill.glyph} />
            </span>
            {skill.name}
          </button>
        ))}
        <button
          type="button"
          className={`cquick__add${choosing ? ' cquick__add--on' : ''}`}
          aria-expanded={choosing}
          aria-label="Choose which tools are here"
          title="Choose which tools are here"
          onClick={() => setChoosing((open) => !open)}
        >
          <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
            <path d="M6 1.5v9M1.5 6h9" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {choosing ? (
        <div className="cquick__list" role="group" aria-label="Tools to keep here">
          {skills.map((skill) => {
            const on = (chosen ?? []).includes(skill.id)
            return (
              <button
                key={skill.id}
                type="button"
                role="checkbox"
                aria-checked={on}
                className={`cquick__option${on ? ' cquick__option--on' : ''}`}
                style={{ '--tile-glow': glowOf.get(skill.id) } as React.CSSProperties}
                onClick={() => flip(skill.id)}
              >
                <span className="cquick__glyph" aria-hidden="true">
                  <SkillGlyph glyph={skill.glyph} />
                </span>
                <span className="cquick__option-name">{skill.name}</span>
                <span className="cquick__tick" aria-hidden="true">
                  {on ? (
                    <svg viewBox="0 0 12 10" focusable="false">
                      <path d="M1 5.2 4.3 8.5 11 1.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : null}
                </span>
              </button>
            )
          })}
        </div>
      ) : null}
    </div>,
    document.body,
  )
}

interface QuickBarProps {
  /** Every tool the rail offers, in the platform's order (which sets the colours). */
  skills: SkillDefinition[]
  /** Why a tool cannot take this, or null when it can. A tool with a reason is greyed out. */
  refuse: (skill: SkillDefinition) => string | null
  onPick: (skill: SkillDefinition, glow: string) => void
}

/**
 * The same tools on a chosen picture (Huanrui, 2026-10-07), over its
 * top-left corner (a React Flow NodeToolbar): the + first, then the ticked
 * tools. Picking one puts that card to the right of the picture with the
 * picture already wired into it. Its own ticked list, apart from the wheel's
 * row (Huanrui, 2026-10-07): what is wanted on a picture is not what is
 * wanted on empty board.
 *
 * Frames had it too for an afternoon, all greyed out until there are frame
 * tools; taken off again the same day.
 */
export function QuickBar({ skills, refuse, onPick }: QuickBarProps) {
  const [chosen, setChosen] = useState<string[] | null>(remembered['quick-picture'])
  const [choosing, setChoosing] = useState(false)

  useEffect(() => {
    if (chosen) return
    let cancelled = false
    void readChosen('quick-picture').then((found) => {
      if (!cancelled) setChosen(found)
    })
    return () => {
      cancelled = true
    }
  }, [chosen])

  useEffect(() => {
    if (!choosing) return
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setChoosing(false)
    }
    window.addEventListener('keydown', escape)
    return () => window.removeEventListener('keydown', escape)
  }, [choosing])

  // The same colour each tool has in the rail.
  const glowOf = useMemo(() => {
    const total = Math.max(skills.length, 1)
    return new Map(skills.map((skill, index) => [skill.id, spectrumColor(index, total)]))
  }, [skills])
  const byId = useMemo(() => new Map(skills.map((skill) => [skill.id, skill])), [skills])
  const shown = (chosen ?? []).map((id) => byId.get(id)).filter((skill): skill is SkillDefinition => Boolean(skill))

  const flip = (id: string) => {
    const next = (chosen ?? []).includes(id) ? (chosen ?? []).filter((one) => one !== id) : [...(chosen ?? []), id]
    setChosen(next)
    keepChosen('quick-picture', next)
  }

  return (
    <div
      className="cquick cquick--bar nodrag nopan"
      // The board must not take these for a press on the picture underneath.
      onPointerDown={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
      onContextMenu={(event) => event.preventDefault()}
    >
      <div className="cquick__row">
        <button
          type="button"
          className={`cquick__add${choosing ? ' cquick__add--on' : ''}`}
          aria-expanded={choosing}
          aria-label="Choose which tools are here"
          title="Choose which tools are here"
          onClick={() => setChoosing((open) => !open)}
        >
          <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
            <path d="M6 1.5v9M1.5 6h9" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
        {shown.map((skill) => {
          const why = refuse(skill)
          return (
            <button
              key={skill.id}
              type="button"
              className={`cquick__skill${why ? ' cquick__skill--off' : ''}`}
              style={{ '--tile-glow': glowOf.get(skill.id) } as React.CSSProperties}
              title={why ?? skill.summary}
              aria-disabled={Boolean(why)}
              onClick={() => {
                if (why) return
                onPick(skill, glowOf.get(skill.id) ?? spectrumColor(0, 1))
              }}
            >
              <span className="cquick__glyph" aria-hidden="true">
                <SkillGlyph glyph={skill.glyph} />
              </span>
              {skill.name}
            </button>
          )
        })}
      </div>

      {choosing ? (
        <div className="cquick__list nowheel" role="group" aria-label="Tools to keep here">
          {skills.map((skill) => {
            const on = (chosen ?? []).includes(skill.id)
            return (
              <button
                key={skill.id}
                type="button"
                role="checkbox"
                aria-checked={on}
                className={`cquick__option${on ? ' cquick__option--on' : ''}`}
                style={{ '--tile-glow': glowOf.get(skill.id) } as React.CSSProperties}
                onClick={() => flip(skill.id)}
              >
                <span className="cquick__glyph" aria-hidden="true">
                  <SkillGlyph glyph={skill.glyph} />
                </span>
                <span className="cquick__option-name">{skill.name}</span>
                <span className="cquick__tick" aria-hidden="true">
                  {on ? (
                    <svg viewBox="0 0 12 10" focusable="false">
                      <path d="M1 5.2 4.3 8.5 11 1.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : null}
                </span>
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
